import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShopCustomerAuthContext = createContext(null);

export const useShopCustomerAuth = () => {
  const context = useContext(ShopCustomerAuthContext);
  if (!context) {
    throw new Error('useShopCustomerAuth must be used within a ShopCustomerAuthProvider');
  }
  return context;
};

const storageKey = (orgId) => `zoea-shop-customer-${orgId}`;

const readStored = (orgId) => {
  try {
    const raw = localStorage.getItem(storageKey(orgId));
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

export const ShopCustomerAuthProvider = ({ orgId, children }) => {
  const stored = readStored(orgId);
  const [token, setToken] = useState(stored?.token || null);
  const [customer, setCustomer] = useState(stored?.customer || null);
  const [loading, setLoading] = useState(!!stored?.token);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    axios.get(`${API}/public/commerce/${orgId}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setCustomer(res.data))
      .catch(() => {
        setToken(null);
        setCustomer(null);
        localStorage.removeItem(storageKey(orgId));
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = (newToken, newCustomer) => {
    setToken(newToken);
    setCustomer(newCustomer);
    localStorage.setItem(storageKey(orgId), JSON.stringify({ token: newToken, customer: newCustomer }));
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/public/commerce/${orgId}/auth/login`, { email, password });
    persist(response.data.access_token, response.data.customer);
    return response.data.customer;
  };

  const register = async (name, email, password, phone) => {
    const response = await axios.post(`${API}/public/commerce/${orgId}/auth/register`, { name, email, password, phone });
    persist(response.data.access_token, response.data.customer);
    return response.data.customer;
  };

  const logout = () => {
    setToken(null);
    setCustomer(null);
    localStorage.removeItem(storageKey(orgId));
  };

  const value = {
    token,
    customer,
    loading,
    isLoggedIn: !!customer,
    login,
    register,
    logout,
  };

  return <ShopCustomerAuthContext.Provider value={value}>{children}</ShopCustomerAuthContext.Provider>;
};

export default ShopCustomerAuthContext;
